/**
 * (c) 2025
 * written by Marco Aveta
 * I-PLAN
 *
 * Menu helper module
 * Handles dynamic loading and rendering of checklist groups and forms
 * Contains all rights checking logic for menu rendering
 */

import * as api from "../../api/apiConnector.js";
import { RightsTreeEnum } from './rightsTreeEnum.js';

/**
 * Load form groups with their associated checklists
 * @param {Function} currentUserFunc - Function to get current user
 * @returns {Array} - Array of groups with checklist information
 */
const loadChecklistGroups = async (currentUserFunc) => {
  try {
    // Una sola chiamata che restituisce tutto
    const checklistResult = await api.sendMessage("darm/formAssignments/getMenuFormsForUser", {
      user: currentUserFunc(),
      body: {} // nessun parametro per ottenere tutti i gruppi
    });

    if (checklistResult?.success && checklistResult?.groupsWithChecklists) {
      return checklistResult.groupsWithChecklists; // già raggruppati!
    }

    return [];
  } catch (error) {
    console.error('Error loading checklist groups:', error);
    return [];
  }
};

/**
 * Generate CheckList menu HTML
 * @param {Array} checklistGroups - Array of groups with forms
 * @param {Object} mod - Module configuration
 * @param {Function} t - Translation function
 * @returns {string} - Generated HTML for the menu
 */
const generateChecklistMenuHtml = (checklistGroups, mod, t) => {
  if (!checklistGroups || !Array.isArray(checklistGroups) || checklistGroups.length === 0) {
    // Fallback al menu semplice se non ci sono gruppi
    return `
      <li>
        <a href="/checklist">
          <div class="menu-icon"><i class="fa ${mod.icon}"></i></div>
          <span class="nav-text">${t(`menu.${mod.key}.title`)}</span>
        </a>
      </li>
    `;
  }

  let menuHtml = `
    <li>
      <a class="has-arrow" href="#">
        <div class="menu-icon"><i class="fa ${mod.icon}"></i></div>
        <span class="nav-text">${t(`menu.${mod.key}.title`)}</span>
      </a>
      <ul class="mm-collapse">`;
  
  // Aggiungiamo ogni gruppo
  checklistGroups.forEach(group => {
    if (group && group.code && group.name && group.forms && Array.isArray(group.forms) && group.forms.length > 0) {
      menuHtml += `
        <li>
          <a class="has-arrow" href="#">
            <span class="nav-text">${group.name.replace(/['"]/g, '')}</span>
          </a>
          <ul class="mm-collapse">`;
      
      // Aggiungiamo tutte le form del gruppo
      group.forms.forEach(form => {
        if (form && form.Id && form.Name) {
          const formName = form.Name.replace(/['"<>&]/g, '').substring(0, 50);
          menuHtml += `
            <li>
              <a class="has-arrow" href="#">${formName}</a>
              <ul class="mm-collapse">
                <li><a href="/form/compile/${form.Id}">${t('menu.checklist.compile', 'Compila')}</a></li>
                <li><a href="/form_builder/dashboard/${form.Id}">${t('menu.checklist.history', 'Storico')}</a></li>
              </ul>
            </li>`;
        }
      });
      
      menuHtml += `</ul></li>`;
    }
  });
  
  menuHtml += `</ul></li>`;
  return menuHtml;
};

/**
 * Handle CheckList menu rendering
 * @param {Object} mod - Module configuration
 * @param {Array} checklistGroups - Array of groups with forms
 * @param {Function} t - Translation function
 * @param {Function} addMenuItem - Function to add menu item to DOM
 * @returns {boolean} - True if menu was handled successfully
 */
const handleChecklistMenu = (mod, checklistGroups, t, addMenuItem) => {
  if (mod.key !== "checklist") {
    return false; // Not a checklist module
  }

  // Skip CheckList module if no groups with checklists
  if (!checklistGroups || checklistGroups.length === 0) {
    return true; // Handled but skipped
  }

  try {
    const menuHtml = generateChecklistMenuHtml(checklistGroups, mod, t);
    addMenuItem(menuHtml);
    return true; // Successfully handled
  } catch (error) {
    console.error("Error generating CheckList menu, using fallback:", error);
    // Fallback di emergenza - menu semplice
    addMenuItem(`
      <li>
        <a href="/checklist">
          <div class="menu-icon"><i class="fa ${mod.icon}"></i></div>
          <span class="nav-text">${t(`menu.${mod.key}.title`)}</span>
        </a>
      </li>
    `);
    return true; // Handled with fallback
  }
};

/**
 * Check if user has ROOT rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has ROOT rights
 */
const hasRootRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read")
  );
};

/**
 * Check if user has administration rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has administration rights or ROOT rights
 */
const hasAdministrationRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return hasRootRights(user) || user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ADMINISTRATION.toLowerCase() && right.action === "read")
  );
};

/**
 * Check if user has handhygiene rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has hand hygiene rights or ROOT rights
 */
const hasHandHygieneRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  // ROOT users have access to everything
  if (hasRootRights(user)) {
    return true;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.HAND_HYGIENE.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.COMPILE_HAND_HYGIENE.toLowerCase() && right.action === "read")
  );
};

/**
 * Check if user has DMS rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has DMS rights or ROOT rights
 */
const hasDMSRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  // ROOT users have access to everything
  if (hasRootRights(user)) {
    return true;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.DMS.toLowerCase() && right.action === "read")
  );
};

/**
 * Check if user has USER_AND_GROUP_MANAGEMENT write rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has USER_AND_GROUP_MANAGEMENT write rights or ROOT rights
 */
const hasUserAndGroupManagementRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.ADMINISTRATION.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.USER_AND_GROUP_MANAGEMENT.toLowerCase() && right.action === "write")
  );
};

/**
 * Check if user has ORGANIZATIONA_CHART write rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has ORGANIZATIONA_CHART write rights or ROOT rights
 */
const hasOrganizationChartRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.ADMINISTRATION.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.ORGANIZATIONA_CHART.toLowerCase() && right.action === "write")
  );
};

/**
 * Check if user has DICTIONARY write rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has DICTIONARY write rights or ROOT rights
 */
const hasDictionaryRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.ADMINISTRATION.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DICTIONARY.toLowerCase() && right.action === "write")
  );
};

/**
 * Check if user has FORM_BUILDER write rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has FORM_BUILDER write rights or ROOT rights
 */
const hasFormBuilderRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DARM.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.FORM_BUILDER.toLowerCase() && right.action === "write")
  );
};

const hasDMSAdminRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DARM.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DMS.toLowerCase() && right.action === "read")
  );
};


const hasValidatorRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DARM.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DMS_VALIDATOR.toLowerCase() && right.action === "write")
  );
};


const hasApprovalRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DARM.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DMS_APPROVAL.toLowerCase() && right.action === "write")
  );
};

const hasEditorRights = (user) => {

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DARM.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DMS_EDITOR.toLowerCase() && right.action === "write")
  );
};

/**
 * Check if user has FORM_ASSIGN write rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has FORM_ASSIGN write rights or ROOT rights
 */
const hasFormAssignRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DARM.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.FORM_BUILDER.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.FORM_ASSIGN.toLowerCase() && right.action === "write")
  );
};

/**
 * Check if user has FORM_VALIDATE write rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has FORM_VALIDATE write rights or ROOT rights
 */
const hasFormValidateRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.DARM.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.FORM_BUILDER.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.FORM_VALIDATE.toLowerCase() && right.action === "write")
  );
};

/**
 * Check if user has RIGHTS_TREE_MANAGEMENT write rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has RIGHTS_TREE_MANAGEMENT write rights or ROOT rights
 */
const hasRightsTreeManagementRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.SECURITY.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.RIGHTS_TREE_MANAGEMENT.toLowerCase() && right.action === "write")
  );
};

/**
 * Check if user has SYSTEM_CONFIGURATION write rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has SYSTEM_CONFIGURATION write rights or ROOT rights
 */
const hasSystemConfigurationRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  // ROOT users have access to everything
  if (hasRootRights(user)) {
    return true;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.ADMINISTRATION.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.SYSTEM_CONFIGURATION.toLowerCase() && right.action === "write")
  );
};

/**
 * Check if user has POWER_BI_REPORTS read rights
 * @param {Object} user - User object containing Rights array
 * @returns {boolean} - True if user has POWER_BI_REPORTS read rights or ROOT rights
 */
const hasPowerBIReportsRights = (user) => {
  if (!user || !user.Rights || !Array.isArray(user.Rights)) {
    return false;
  }

  // ROOT users have access to everything
  if (hasRootRights(user)) {
    return true;
  }

  return user.Rights.some(right =>
    (right.nodeId.toLowerCase() === RightsTreeEnum.DARM.toLowerCase() && right.action === "read") ||
    (right.nodeId.toLowerCase() === RightsTreeEnum.POWER_BI_REPORTS.toLowerCase() && right.action === "read")
  );
};

/**
 * Helper function to check specific rights based on right type
 * @param {Object} user - User object containing Rights array
 * @param {string} rightType - Type of right to check
 * @returns {boolean} - True if user has the specified right
 */
const hasSpecificRight = (user, rightType) => {
  // ROOT users have access to everything
  if (hasRootRights(user)) {
    return true;
  }

  switch (rightType) {
    case "userAndGroupManagement":
      return hasUserAndGroupManagementRights(user);
    case "organizationChart":
      return hasOrganizationChartRights(user);
    case "dictionary":
      return hasDictionaryRights(user);
    case "formBuilder":
      return hasFormBuilderRights(user);
    case "formAssign":
      return hasFormAssignRights(user);
    case "formValidate":
      return hasFormValidateRights(user);
    case "rightsTreeManagement":
      return hasRightsTreeManagementRights(user);
    case "systemConfiguration":
      return hasSystemConfigurationRights(user);
    case "powerBIReports":
      return hasPowerBIReportsRights(user);
    default:
      return true;
  }
};

export {
  loadChecklistGroups,
  generateChecklistMenuHtml,
  handleChecklistMenu,
  hasRootRights,
  hasAdministrationRights,
  hasHandHygieneRights,
  hasDMSRights,
  hasUserAndGroupManagementRights,
  hasOrganizationChartRights,
  hasDictionaryRights,
  hasFormBuilderRights,
  hasDMSAdminRights,
  hasValidatorRights,
  hasApprovalRights,
  hasEditorRights,
  hasFormAssignRights,
  hasFormValidateRights,
  hasRightsTreeManagementRights,
  hasSystemConfigurationRights,
  hasPowerBIReportsRights,
  hasSpecificRight
}; 